import { ErrorReportingUtils } from '@mm/plugins';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import sessionCRUDActions from '@mm/state/actions/SessionCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import {
  AnonymousBot,
  AnonymousBotController,
} from '@mm/state/controllers/AnonymousBotController';
import { SESSION_KEY_VERSION } from '@mm/state/controllers/AuthenticatedController';
import { StoreState } from '@mm/state/store-interfaces';
import { LocalStorageService } from '@mm/utils';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { LocalVar } from '../../LocalVar';

const localToken = LocalVar({ name: 'standalone_token' });

export const useStandaloneBot = (source: string) => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const companyCRUDActions = useCompanyCRUDActions();
  const widgetCRUDActions = useWidgetCRUDActions();

  const token = localToken.get();

  const [bot, setBot] = useState<undefined | AnonymousBot>();
  const [busy, setBusy] = useState(false);

  const onBotReceived = async (bot: AnonymousBot) => {
    const { authToken, companyId } = bot;

    if (authToken) {
      const session = {
        authToken,
        userAttributes: { activeCompanyId: companyId },
        isGuest: true,
      };

      // put guest session in memory & storage
      // this is required as front-end requests are coupled to state (image upload requires active companyId, etc...)
      dispatch({
        type: sessionCRUDActions.retrievedAction,
        entity: session,
      });
      LocalStorageService.setItem(SESSION_KEY_VERSION, JSON.stringify(session));
    }

    // retrieve all data needed
    await Promise.all([
      companyCRUDActions.getById(companyId),
      widgetCRUDActions.retrieveAll(bot.page.id),
    ]);

    setBot(bot);
  };

  const createBot = useCallback(() => {
    setBusy(true);
    try {
      return AnonymousBotController.create({ source });
    } catch (err) {
      ErrorReportingUtils.reportError('Standalone Bot Error: fetch bot', {
        extraData: { err: JSON.stringify(err) },
      });
    } finally {
      setBusy(false);
    }
  }, []);

  const fetchBot = useCallback(id => {
    setBusy(true);
    try {
      return AnonymousBotController.get(id);
    } catch (err) {
      ErrorReportingUtils.reportError('Standalone Bot Error: fetch bot', {
        extraData: { err: JSON.stringify(err) },
      });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      let bot;

      if (!token) {
        const res = await createBot();
        bot = res?.bot;
        if (bot?.standaloneToken) {
          localToken.set(bot.standaloneToken);
        }
      }

      if (token && !bot && !busy) {
        bot = await fetchBot(token);
      }

      if (bot) {
        await onBotReceived(bot);
      }
    })();
  }, []);

  useEffect(() => {
    // old name in case present
    LocalStorageService.removeItem('client_uuid');
  }, []);

  return {
    localToken,
    bot,
    busy,
  };
};
